import { AppShell } from './components/layout/AppShell';
import { ChartZone } from './components/chart/ChartZone';
import { WorkZone } from './components/work/WorkZone';
import { MarketView } from './views/MarketView';
import { ReviewView } from './views/ReviewView';
import { NoteBrowser } from './views/NoteBrowser';
import { LoginView } from './components/auth/LoginView';
import { SelectionProvider } from './lib/selection';
import { ThemeProvider, useTheme } from './lib/themeContext';
import { AuthProvider, useAuth } from './lib/auth';
import type { ViewId } from './lib/app';

/**
 * 图表区需要当前主题（lightweight-charts 不读 CSS 变量，只接受具体色值），
 * 而主题状态持有在 AppShell 内。为避免把它作为 prop 层层透传，
 * 提升为独立的 ThemeProvider —— AppShell 是它的写入方，其余子树是读取方。
 */
function ChartSlot() {
  const { theme } = useTheme();
  return <ChartZone theme={theme} />;
}

/**
 * 图表视图：图表区在上、工作区（新闻/笔记）在下，纵向堆叠。
 *
 * 改前两者由 SplitPane 上下分栏、比例可拖拽；改为整页滚动后不再分栏 ——
 * 图表高度由档位固定（lightweight-charts 必须有确定高度），工作区紧随其后
 * 一路滑到底即可，不再被挤在"剩余那点空间"里。
 */
function ChartView() {
  return (
    <div className="flex flex-col">
      <ChartSlot />
      <WorkZone />
    </div>
  );
}

function Shell() {
  const { ready, user } = useAuth();

  // ① 尚未确认登录态 → 极简占位，避免闪一下登录页再闪回应用
  if (!ready) {
    return (
      <div className="grid min-h-screen place-items-center" style={{ background: 'var(--bg-base)' }}>
        <span className="text-[13px]" style={{ color: 'var(--text-3)' }}>加载中…</span>
      </div>
    );
  }

  // ② 未登录 → 只渲染登录页，不挂载任何业务组件
  if (!user) return <LoginView />;

  return (
    <AppShell
      chartZone={<ChartView />}
      fullView={(view: ViewId, onNavigate: (v: ViewId) => void) => {
        switch (view) {
          case 'market': return <MarketView onNavigate={onNavigate} />;
          case 'review': return <ReviewView onNavigate={onNavigate} />;
          case 'note': return <NoteBrowser />;
          case 'work': return <WorkZone />;
          default: return null;
        }
      }}
    />
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <SelectionProvider>
          <Shell />
        </SelectionProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}
